// Import Libraries
import React, { useContext, useState } from 'react';
import { Avatar, AvatarGroup, Box, IconButton, Stack, Tooltip } from '@mui/material';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';

// Import Components
import CollaboratorDropdown from '../Project/collaboratorDropdown.js';

// Context
import ScriptContext from '../../../context/Script';
import ProjectViewContext from '../../../context/ProjectView';

// Import Services
import utilities from '../../../services/utilities';

// Max avatars before AvatarGroup collapses the rest into "+N".
const MAX_AVATARS = 5;

// Construct Component
const CollaboratorAvatars = () => {
  const { script, setScript } = useContext(ScriptContext);
  const { activeCollaborators } = useContext(ProjectViewContext);
  const [showDropdown, setShowDropdown] = useState(false);

  const collaborators = script?.collaborators;
  if (!collaborators) return null;

  const people = [
    collaborators.owner,
    ...(collaborators.editors || []),
    ...(collaborators.viewers || []),
  ].filter(Boolean);

  // Collaborators currently in the project show their color; everyone else is
  // shown neutral.
  const avatarColors = (collab) => {
    const isActive = activeCollaborators ? activeCollaborators.includes(collab.user_id) : true;
    if (!isActive || !collab.color) return { bgcolor: 'grey.200', color: 'text.primary' };
    return {
      bgcolor: collab.color,
      color: utilities.isDarkColor(collab.color) ? 'common.white' : 'common.black',
    };
  };

  const isOwner = script.level === 'owner';

  return (
    <Stack direction="row" alignItems="center">
      <AvatarGroup max={MAX_AVATARS} spacing={8}>
        {people.map((collab) => (
          <Tooltip key={collab.user_id || collab.id} title={collab.name || collab.email}>
            <Avatar alt={collab.name || collab.email} sx={avatarColors(collab)}>
              {collab.initials}
            </Avatar>
          </Tooltip>
        ))}
      </AvatarGroup>

      {isOwner && (
        <Box sx={{ position: 'relative' }}>
          <Tooltip title="Add collaborators">
            <Avatar
              component={IconButton}
              variant="dashed"
              aria-label="Add collaborators"
              aria-expanded={showDropdown}
              onClick={() => setShowDropdown((open) => !open)}
            >
              <PersonAddAlt1OutlinedIcon fontSize="small" />
            </Avatar>
          </Tooltip>
          {showDropdown && (
            <CollaboratorDropdown
              script={script}
              updateScript={(updated) => setScript((prev) => ({ ...prev, ...updated }))}
              setShow={setShowDropdown}
            />
          )}
        </Box>
      )}
    </Stack>
  );
};

export default CollaboratorAvatars;
